import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Mail, ChevronDown } from "lucide-react";

const NAV_GROUPS = [
    {
        label: "The Firm",
        testId: "the-firm",
        items: [
            { to: "/about", label: "About" },
            { to: "/values", label: "Our Values" },
            { to: "/people", label: "People" },
            { to: "/careers", label: "Careers" }
        ]
    },
    {
        label: "Practice",
        testId: "practice",
        items: [
            { to: "/practice-areas", label: "Overview" },
            { to: "/estate-planning", label: "Estate Planning" },
            { to: "/elder-law", label: "Elder Law" },
            { to: "/real-estate-law", label: "Real Estate" },
            { to: "/business-law", label: "Business Law" }
        ]
    },
    {
        label: "Resources",
        testId: "resources",
        items: [
            { to: "/blog", label: "Insights" },
            { to: "/news", label: "News" },
            { to: "/legal-links", label: "Legal Links" }
        ]
    }
];

const NAV_SINGLES = [];

function DesktopNavGroup({ group }) {
    return (
        <div className="relative group" data-testid={`nav-group-${group.testId}`}>
            <button
                type="button"
                className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.18em] text-acre-ink hover:text-acre-secondary transition-colors py-6"
                aria-haspopup="true"
                aria-expanded="false"
            >
                {group.label}
                <ChevronDown size={12} strokeWidth={2} className="mt-[1px] opacity-70 transition-transform duration-200 group-hover:rotate-180" />
            </button>
            <div
                className="absolute top-full left-1/2 -translate-x-1/2 min-w-[220px] pt-1 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50"
                role="menu"
            >
                <div className="bg-white border border-acre-line shadow-lg py-2">
                    {group.items.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            role="menuitem"
                            data-testid={`nav-${group.testId}-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `block px-5 py-2.5 text-[13px] tracking-wide transition-colors ${
                                    isActive
                                        ? "text-acre-secondary font-semibold bg-acre-bg"
                                        : "text-acre-ink hover:text-acre-secondary hover:bg-acre-bg"
                                }`
                            }
                        >
                            {it.label}
                        </NavLink>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <>
            {/* Slim top bar */}
            <div
                data-testid="topbar"
                className="hidden lg:block bg-acre-topbar text-white/90 text-[12px]"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 h-9 flex items-center justify-end gap-6">
                    <a
                        href="mailto:info@chesterton.law"
                        data-testid="topbar-email"
                        className="inline-flex items-center gap-2 hover:text-white transition-colors"
                    >
                        <Mail size={13} strokeWidth={1.5} />
                        info@chesterton.law
                    </a>
                </div>
            </div>

            <header
                data-testid="site-header"
                className={`sticky top-0 z-50 transition-all duration-300 ${
                    scrolled
                        ? "bg-white/95 backdrop-blur border-b border-acre-line"
                        : "bg-white border-b border-acre-line"
                }`}
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 h-[76px] flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="header-logo"
                        className="group flex flex-col leading-none select-none"
                        onClick={() => setOpen(false)}
                    >
                        <span className="font-serif-display text-[30px] tracking-tight text-acre-ink group-hover:text-acre-secondary transition-colors">
                            Chesterton
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-acre-muted mt-1.5">
                            Attorneys at Law
                        </span>
                    </Link>

                    <nav
                        className="hidden lg:flex items-center gap-8 xl:gap-10"
                        aria-label="Primary"
                    >
                        {NAV_GROUPS.map((g) => (
                            <DesktopNavGroup key={g.label} group={g} />
                        ))}
                        {NAV_SINGLES.map((s) => (
                            <NavLink
                                key={s.to}
                                to={s.to}
                                data-testid={`nav-${s.testId}`}
                                className={({ isActive }) =>
                                    `text-[12px] uppercase tracking-[0.18em] transition-colors duration-200 whitespace-nowrap py-6 ${
                                        isActive
                                            ? "text-acre-secondary font-semibold"
                                            : "text-acre-ink hover:text-acre-secondary"
                                    }`
                                }
                            >
                                {s.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center">
                        <Link
                            to="/contact"
                            data-testid="header-cta-consult"
                            className="inline-flex items-center bg-acre-primary text-white px-6 py-3.5 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-acre-primaryHover transition-colors"
                        >
                            Contact
                        </Link>
                    </div>

                    <button
                        data-testid="mobile-menu-toggle"
                        className="lg:hidden p-2 -mr-2 text-acre-ink"
                        aria-label="Toggle navigation"
                        onClick={() => setOpen((v) => !v)}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>

                {open && (
                    <div
                        data-testid="mobile-menu"
                        className="lg:hidden border-t border-acre-line bg-white max-h-[calc(100vh-76px)] overflow-y-auto"
                    >
                        <div className="px-6 py-6 flex flex-col gap-6">
                            {NAV_GROUPS.map((g) => (
                                <div key={g.label}>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-acre-muted mb-2 pb-2 border-b border-acre-line">
                                        {g.label}
                                    </div>
                                    <div className="flex flex-col">
                                        {g.items.map((it) => (
                                            <NavLink
                                                key={it.to}
                                                to={it.to}
                                                onClick={() => setOpen(false)}
                                                data-testid={`mobile-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                className={({ isActive }) =>
                                                    `py-2.5 text-[15px] ${isActive ? "text-acre-secondary font-semibold" : "text-acre-ink"}`
                                                }
                                            >
                                                {it.label}
                                            </NavLink>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {NAV_SINGLES.map((s) => (
                                <NavLink
                                    key={s.to}
                                    to={s.to}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-${s.testId}`}
                                    className={({ isActive }) =>
                                        `py-3 text-[13px] uppercase tracking-[0.18em] border-t border-acre-line ${
                                            isActive ? "text-acre-secondary font-semibold" : "text-acre-ink"
                                        }`
                                    }
                                >
                                    {s.label}
                                </NavLink>
                            ))}
                            <a
                                href="mailto:info@chesterton.law"
                                data-testid="mobile-email-link"
                                className="py-3 text-sm text-acre-ink flex items-center gap-2 border-t border-acre-line"
                            >
                                <Mail size={14} /> info@chesterton.law
                            </a>
                            <Link
                                to="/contact"
                                onClick={() => setOpen(false)}
                                data-testid="mobile-cta-consult"
                                className="mt-1 inline-flex items-center justify-center bg-acre-primary text-white px-5 py-3.5 text-[11px] uppercase tracking-[0.22em] font-semibold"
                            >
                                Contact
                            </Link>
                        </div>
                    </div>
                )}
            </header>
        </>
    );
}
